import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { EnvironmentDetailComponent } from '../environment-detail/environment-detail.component';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  AdminApiService,
  AdminEnvironment,
  EnvironmentListFilterField,
  EnvironmentListPage,
} from 'src/app/shared/services/admin-api.service';

type EnvironmentFilterValueOption = { value: string; label: string };

@Component({
  standalone: false,
  selector: 'app-manage-environments',
  templateUrl: './manage-environments.component.html',
  styleUrls: ['./manage-environments.component.scss'],
})
export class ManageEnvironmentsComponent extends AdminSectionBaseComponent implements OnInit, OnDestroy {
  environments: AdminEnvironment[] = [];
  environmentsFilterField: EnvironmentListFilterField = 'name';
  environmentsFilterValue = '';
  environmentFilterValueOptions: EnvironmentFilterValueOption[] = [];
  environmentsPage = 1;
  environmentsTotalItems = 0;
  environmentsTotalPages = 1;
  readonly environmentsPageSize = 20;
  readonly environmentFilterOptions: Array<{ value: EnvironmentListFilterField; label: string }> = [
    { value: 'name', label: 'Environment' },
    { value: 'owner', label: 'Owner' },
    { value: 'status', label: 'Status' },
    { value: 'region', label: 'Region' },
  ];
  readonly environmentStatusOptions: EnvironmentFilterValueOption[] = [
    { value: 'PROVISIONING', label: 'PROVISIONING' },
    { value: 'INITIALIZING', label: 'INITIALIZING' },
    { value: 'RUNNING', label: 'RUNNING' },
    { value: 'STARTING', label: 'STARTING' },
    { value: 'STOPPING', label: 'STOPPING' },
    { value: 'STOPPED', label: 'STOPPED' },
    { value: 'TERMINATING', label: 'TERMINATING' },
    { value: 'TERMINATED', label: 'TERMINATED' },
    { value: 'REPAIRING', label: 'REPAIRING' },
    { value: 'FAILED', label: 'FAILED' },
  ];
  readonly environmentRegionOptions: EnvironmentFilterValueOption[] = [
    { value: 'us-central', label: 'us-central' },
    { value: 'us-east', label: 'us-east' },
    { value: 'us-west', label: 'us-west' },
    { value: 'southamerica-east', label: 'southamerica-east' },
    { value: 'europe-west', label: 'europe-west' },
    { value: 'asia-south', label: 'asia-south' },
    { value: 'asia-northeast', label: 'asia-northeast' },
    { value: 'asia-southeast', label: 'asia-southeast' },
    { value: 'australia-southeast', label: 'australia-southeast' },
  ];
  deleteConfirmEnv: AdminEnvironment | null = null;
  private environmentsFilterDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
  }

  ngOnInit(): void {
    this.updateEnvironmentFilterValueOptions();
    this.refreshEnvironments();
  }

  ngOnDestroy(): void {
    this.clearEnvironmentsFilterDebounce();
  }

  refreshEnvironments(): void {
    this.loadEnvironmentsPage(this.environmentsPage);
  }

  getDisplayedEnvironments(): AdminEnvironment[] {
    return this.environments;
  }

  onEnvironmentsFilterFieldChange(): void {
    this.updateEnvironmentFilterValueOptions();

    if (!this.usesSelectableEnvironmentFilter()) {
      this.scheduleEnvironmentsFilter();
      return;
    }

    const allowedValues = new Set(this.environmentFilterValueOptions.map((option) => option.value));
    if (!allowedValues.has(this.environmentsFilterValue)) {
      this.environmentsFilterValue = '';
    }

    this.scheduleEnvironmentsFilter();
  }

  onEnvironmentsFilterValueChange(): void {
    this.scheduleEnvironmentsFilter();
  }

  applyEnvironmentsFilterImmediately(): void {
    this.clearEnvironmentsFilterDebounce();
    this.loadEnvironmentsPage(1);
  }

  clearEnvironmentsFilter(): void {
    this.environmentsFilterField = 'name';
    this.environmentsFilterValue = '';
    this.updateEnvironmentFilterValueOptions();
    this.clearEnvironmentsFilterDebounce();
    this.loadEnvironmentsPage(1);
  }

  usesSelectableEnvironmentFilter(): boolean {
    return this.environmentsFilterField === 'status'
      || this.environmentsFilterField === 'region';
  }

  getEnvironmentFilterPlaceholder(): string {
    switch (this.environmentsFilterField) {
      case 'name':
        return 'Environment name';
      case 'owner':
        return 'Owner email';
      default:
        return 'Value';
    }
  }

  goToPreviousEnvironmentsPage(): void {
    if (this.environmentsPage > 1) {
      this.loadEnvironmentsPage(this.environmentsPage - 1);
    }
  }

  goToNextEnvironmentsPage(): void {
    if (this.environmentsPage < this.environmentsTotalPages) {
      this.loadEnvironmentsPage(this.environmentsPage + 1);
    }
  }

  private loadEnvironmentsPage(page: number): void {
    const requestedPage = Math.max(1, page);
    const filterValue = this.environmentsFilterValue.trim();
    this.trackApiCall(this.adminApiService.listEnvironments(this.token, {
      page: requestedPage,
      pageSize: this.environmentsPageSize,
      filterField: filterValue ? this.environmentsFilterField : undefined,
      filterValue: filterValue || undefined,
    })).subscribe({
      next: (result: EnvironmentListPage) => {
        this.environments = result.items;
        this.environmentsPage = result.page;
        this.environmentsTotalItems = result.totalItems;
        this.environmentsTotalPages = result.totalPages;
      },
      error: (error) => this.handleError(error),
    });
  }

  private updateEnvironmentFilterValueOptions(): void {
    if (this.environmentsFilterField === 'status') {
      this.environmentFilterValueOptions = this.environmentStatusOptions;
      return;
    }

    if (this.environmentsFilterField === 'region') {
      this.environmentFilterValueOptions = this.environmentRegionOptions;
      return;
    }

    this.environmentFilterValueOptions = [];
  }

  private scheduleEnvironmentsFilter(): void {
    this.clearEnvironmentsFilterDebounce();
    this.environmentsFilterDebounceHandle = setTimeout(() => {
      this.environmentsFilterDebounceHandle = null;
      this.loadEnvironmentsPage(1);
    }, 1000);
  }

  private clearEnvironmentsFilterDebounce(): void {
    if (this.environmentsFilterDebounceHandle !== null) {
      clearTimeout(this.environmentsFilterDebounceHandle);
      this.environmentsFilterDebounceHandle = null;
    }
  }

  getEnvironmentAge(createDate: string): string {
    const created = new Date(createDate);
    const diffMs = Date.now() - created.getTime();
    if (isNaN(diffMs)) { return 'Unknown'; }
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) { return `${days}d ${hours}h`; }
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  restartEnvironment(env: AdminEnvironment): void {
    const actionKey = this.envRestartActionKey(env.name);
    this.trackActionCall(
      actionKey,
      this.adminApiService.modifyEnvironments(this.token, [
        { operation: 'restart', environment: env.name, owner: env.owner },
      ]),
    ).subscribe({
      next: (results) => {
        const failed = (results || []).find((r) => r && r.status === 'failed');
        if (failed) {
          this.showError(failed.reason || `Failed to restart environment ${env.name}.`);
          return;
        }
        this.showSuccess(`Environment ${env.name} restarted.`);
        this.refreshEnvironments();
      },
      error: (error) => this.handleError(error),
    });
  }

  stopEnvironment(env: AdminEnvironment): void {
    const actionKey = this.envStopActionKey(env.name);
    this.trackActionCall(
      actionKey,
      this.adminApiService.modifyEnvironments(this.token, [
        { operation: 'stop', environment: env.name, owner: env.owner },
      ]),
    ).subscribe({
      next: (results) => {
        const failed = (results || []).find((r) => r && r.status === 'failed');
        if (failed) {
          this.showError(failed.reason || `Failed to stop environment ${env.name}.`);
          return;
        }
        this.showSuccess(`Environment ${env.name} stopped.`);
        this.refreshEnvironments();
      },
      error: (error) => this.handleError(error),
    });
  }

  confirmDeleteEnvironment(env: AdminEnvironment): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Environment',
        message: `Are you sure you want to permanently delete this environment? Owner: ${env.owner}`,
        itemName: env.name
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.deleteEnvironment(env);
      }
    });
  }

  deleteEnvironment(env: AdminEnvironment): void {
    const actionKey = this.envDeleteActionKey(env.name);
    this.trackActionCall(
      actionKey,
      this.adminApiService.modifyEnvironments(this.token, [
        { operation: 'delete', environment: env.name, owner: env.owner },
      ]),
    ).subscribe({
      next: (results) => {
        const failed = (results || []).find((r) => r && r.status === 'failed');
        if (failed) {
          this.showError(failed.reason || `Failed to delete environment ${env.name}.`);
          return;
        }
        this.showSuccess(`Environment ${env.name} deleted.`);
        this.refreshEnvironments();
      },
      error: (error) => this.handleError(error),
    });
  }

  viewEnvironment(env: AdminEnvironment): void {
    this.dialog.open(EnvironmentDetailComponent, {
      width: 'min(1100px, 94vw)',
      maxWidth: '94vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: ['app-dialog-panel', 'environment-detail-dialog-panel'],
      data: {
        owner: env.owner,
        name: env.name,
      },
    }).afterClosed().subscribe(() => {
      this.refreshEnvironments();
    });
  }

  canRestart(env: AdminEnvironment): boolean {
    const s = env.state.toLowerCase();
    return s === 'stopped' || s === 'failed';
  }

  canStop(env: AdminEnvironment): boolean {
    const s = env.state.toLowerCase();
    return s === 'running';
  }

  canKill(env: AdminEnvironment): boolean {
    const s = env.state.toLowerCase();
    return s !== 'terminating' && s !== 'terminated';
  }

  envRestartActionKey(envName: string): string { return `env-restart:${envName}`; }
  envStopActionKey(envName: string): string { return `env-stop:${envName}`; }
  envDeleteActionKey(envName: string): string { return `env-delete:${envName}`; }

  trackByEnvName(_index: number, env: AdminEnvironment): string {
    return env.name;
  }
}
